import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import CloudIcon from '@mui/icons-material/Cloud';
import DevicesIcon from '@mui/icons-material/Devices';
import MergeIcon from '@mui/icons-material/MergeType';
import './SyncConflictDialog.css';

// Sync resolution options
export const SYNC_RESOLUTION = {
  CLOUD: 'cloud',      // Server wins - use cloud data
  DEVICE: 'device',    // Local wins - use this device's data
  MERGE: 'merge'       // Union merge - keep all symbols from both
};

const SyncConflictDialog = ({
  isOpen,
  onResolve,
  localSymbols = [],
  serverSymbols = [],
  localOnlySymbols = [],
  serverOnlySymbols = []
}) => {
  const [selectedOption, setSelectedOption] = useState(SYNC_RESOLUTION.CLOUD);
  const [rememberChoice, setRememberChoice] = useState(false);

  const handleApply = () => {
    onResolve({
      resolution: selectedOption,
      rememberChoice
    });
  };

  const handleCancel = () => {
    // Default to cloud if cancelled
    onResolve({
      resolution: SYNC_RESOLUTION.CLOUD,
      rememberChoice: false
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      className="sync-conflict-modal"
      overlayClassName="sync-conflict-overlay"
      shouldCloseOnOverlayClick={false}
    >
      <div className="sync-conflict-content">
        <h2 className="sync-conflict-title">Watchlist Sync</h2>

        <p className="sync-conflict-description">
          Your watchlist on this device differs from your cloud data.
        </p>

        <div className="sync-conflict-summary">
          <div className="sync-summary-item">
            <DevicesIcon className="sync-summary-icon device" />
            <span>This device: <strong>{localSymbols.length}</strong> stocks</span>
            {localOnlySymbols.length > 0 && (
              <span className="sync-summary-diff">
                ({localOnlySymbols.length} not in cloud)
              </span>
            )}
          </div>
          <div className="sync-summary-item">
            <CloudIcon className="sync-summary-icon cloud" />
            <span>Cloud: <strong>{serverSymbols.length}</strong> stocks</span>
            {serverOnlySymbols.length > 0 && (
              <span className="sync-summary-diff">
                ({serverOnlySymbols.length} not on this device)
              </span>
            )}
          </div>
        </div>

        <div className="sync-conflict-options">
          <label
            className={`sync-option ${selectedOption === SYNC_RESOLUTION.CLOUD ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="syncResolution"
              value={SYNC_RESOLUTION.CLOUD}
              checked={selectedOption === SYNC_RESOLUTION.CLOUD}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <CloudIcon className="sync-option-icon" />
            <div className="sync-option-text">
              <span className="sync-option-label">Use cloud data</span>
              <span className="sync-option-recommended">(recommended)</span>
            </div>
          </label>

          <label
            className={`sync-option ${selectedOption === SYNC_RESOLUTION.DEVICE ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="syncResolution"
              value={SYNC_RESOLUTION.DEVICE}
              checked={selectedOption === SYNC_RESOLUTION.DEVICE}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <DevicesIcon className="sync-option-icon" />
            <div className="sync-option-text">
              <span className="sync-option-label">Use this device's data</span>
            </div>
          </label>

          <label
            className={`sync-option ${selectedOption === SYNC_RESOLUTION.MERGE ? 'selected' : ''}`}
          >
            <input
              type="radio"
              name="syncResolution"
              value={SYNC_RESOLUTION.MERGE}
              checked={selectedOption === SYNC_RESOLUTION.MERGE}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <MergeIcon className="sync-option-icon" />
            <div className="sync-option-text">
              <span className="sync-option-label">Merge both</span>
              <span className="sync-option-description">(keep all stocks)</span>
            </div>
          </label>
        </div>

        <label className="sync-remember-choice">
          <input
            type="checkbox"
            checked={rememberChoice}
            onChange={(e) => setRememberChoice(e.target.checked)}
          />
          <span>Remember my choice</span>
        </label>

        <div className="sync-conflict-actions">
          <button
            className="sync-button sync-button-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="sync-button sync-button-primary"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

SyncConflictDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onResolve: PropTypes.func.isRequired,
  localSymbols: PropTypes.array,
  serverSymbols: PropTypes.array,
  localOnlySymbols: PropTypes.array,
  serverOnlySymbols: PropTypes.array
};

export default SyncConflictDialog;
